import { format } from "date-fns";
import { vi } from "date-fns/locale";

const vndFormatter = new Intl.NumberFormat("vi-VN", { style: "currency", currency: "VND" });

/** Full VND amount: "1.867.458.603 \u20ab" (vi-VN currency style). */
export function formatVND(v: number | null | undefined): string {
  if (v == null) return "";
  // Intl.NumberFormat vi-VN emits U+00A0 (non-breaking space) before the currency symbol
  // Replace it with a regular space for consistent string handling
  return vndFormatter.format(v).replace(/\u00a0/g, " ");
}

/**
 * Compact VND for list/table density: <1M \u2192 "590k", \u22651M \u2192 "4.7 tr".
 * Mirrors FE bk OrdersPage table convention.
 */
export function formatVNDCompact(v: number | null | undefined): string {
  if (v == null) return "";
  const n = Number(v);
  if (!Number.isFinite(n) || n === 0) return "0 \u0111";
  const abs = Math.abs(n);
  if (abs >= 1_000_000) {
    const tr = n / 1_000_000;
    const rounded = Math.round(tr * 10) / 10;
    return `${rounded % 1 === 0 ? rounded.toFixed(0) : rounded.toFixed(1)} tr`;
  }
  if (abs >= 1_000) return `${Math.round(n / 1_000)}k`;
  return `${n} \u0111`;
}

/**
 * Compact VND in dashboard-chart style: <1K \u2192 raw, <1M \u2192 "350K", <1B \u2192 "1.2M",
 * else "2.5B". Uppercase suffix preferred on charts so readers parse
 * faster at small font sizes.
 */
export function formatVNDChart(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  const abs = Math.abs(v);
  if (abs >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(v);
}

/**
 * Đọc số có phần lẻ theo cách người Việt gõ: "7,5" và "7.5" đều là 7,5 (lỗi
 * 24/09/2026 — Number("7,5") ra NaN, parseFloat("7,5") ra 7). Ô trống → null;
 * có chữ lạ hay hai dấu phân cách → NaN, để bên gọi báo lỗi thay vì lưu bừa.
 * Chỉ dùng cho ô phần trăm / số giờ — ô tiền dùng MoneyInput.
 */
export function parseDecimalVN(raw: string): number | null {
  const s = raw.trim().replace(",", ".");
  if (s === "") return null;
  if (!/^-?(\d+\.?\d*|\.\d+)$/.test(s)) return Number.NaN;
  return Number(s);
}

/**
 * Đọc ô tiền / ô đếm đang giữ dạng chữ: chỉ lấy chữ số, "1.500.000" là
 * 1500000 — cùng cách MoneyInput đọc, để số lưu đúng số ô đang hiện (lỗi
 * 24/09/2026). Không có chữ số nào → null.
 */
export function parseDigitsVN(raw: string): number | null {
  const digits = raw.replace(/\D/g, "");
  return digits === "" ? null : Number(digits);
}

/** Plain integer with vi-VN thousands separator: 1335 \u2192 "1.335". */
export function formatIntVN(n: number | null | undefined): string {
  return Number(n ?? 0).toLocaleString("vi-VN");
}

/**
 * VND in the legacy "\u0111" suffix style: 1234567 \u2192 "1.234.567\u0111".
 * Matches FE bk inline-rendering (no space before symbol, lowercase "\u0111").
 * Prefer `formatVND` for new code \u2014 it uses Intl currency style ("1.234.567 \u20ab")
 * which is more correct typographically, but this helper exists so call
 * sites that build legacy-styled labels don't reach for raw toLocaleString.
 */
export function formatVNDShort(n: number | null | undefined): string {
  if (n == null) return "";
  return `${formatIntVN(n)}\u0111`;
}

/** "2025-12" \u2192 "T12/25" \u2014 terse chart axis label. */
export function formatMonthLabel(m: string): string {
  const [y = "", mm = ""] = m.split("-");
  return `T${Number(mm)}/${y.slice(2)}`;
}

/** Date only, "dd/MM/yyyy" in vi locale. */
export function formatDate(d: Date | string | null): string {
  if (!d) return "";
  return format(typeof d === "string" ? new Date(d) : d, "dd/MM/yyyy", { locale: vi });
}

/** Date + time, "dd/MM/yyyy HH:mm" in vi locale. */
export function formatDateTime(d: Date | string | null): string {
  if (!d) return "";
  return format(typeof d === "string" ? new Date(d) : d, "dd/MM/yyyy HH:mm", { locale: vi });
}
